import { ColumnType } from '@dynamika-shared/ui/Table/Table.types';
import { ClinicalTrialType } from '@dynamika-shared/types/clinicalTrials';
import { StatusPill } from '@dynamika-shared/ui/StatusPill/StatusPill';
import { d7Format } from '@dynamika-shared/utils/date';
import { Button } from '@dynamika-shared/ui/Button/Button';
import ChevronIcon from '@dynamika-shared/ui/icons/Chevron';
import { getValueWithTooltip } from '@dynamika-shared/ui/Table/getValueWithTooltip';

type MakeClinicalTrialsColumnsType = {
  onClinicalTrialClick: (clinicalTrial: ClinicalTrialType) => void;
};

const LENGTH_FOR_TOOLTIP = 10;

export function makeClinicalTrialsColumns(
  props: MakeClinicalTrialsColumnsType
): ColumnType<ClinicalTrialType>[] {
  return [
    {
      name: 'name',
      header() {
        return 'Clinical Trial Name';
      },

      loading: index => {
        const width = Number(index) % 2 ? `w-[${20}rem]` : `w-[${17}rem]`;
        return (
          <div
            className={`h-2 ${width} animate-pulse rounded-[5px] bg-brightGray`}
          ></div>
        );
      },
      getValue: getValueWithTooltip('name', LENGTH_FOR_TOOLTIP),
      hasSorting: true,
      cellClassName: 'max-w-full min-w-0 grow',
      tdClassName: 'min-w-full max-w-[30rem] mr-1',
      thClassName: 'w-[20.5%] min-w-[20.5%] max-w-[20.5%]',
    },
    {
      name: 'sponsorName',
      header() {
        return 'Sponsor';
      },
      loading: index => {
        const width = Number(index) % 2 ? `w-[${17}rem]` : `w-[${13}rem]`;
        return (
          <div
            className={`h-2 ${width} animate-pulse rounded-[5px] bg-brightGray`}
          ></div>
        );
      },
      getValue: getValueWithTooltip('sponsorName', LENGTH_FOR_TOOLTIP),
      hasSorting: true,
      cellClassName: 'max-w-full min-w-0 grow',
      tdClassName: 'min-w-full max-w-[25rem]',
      thClassName: 'w-[17.3%] min-w-[17.3%] max-w-[17.3%]',
    },
    {
      name: 'startDate',
      header() {
        return 'Start Date';
      },

      loading: () => (
        <div
          className={'h-2 w-[10rem] animate-pulse rounded-[5px] bg-brightGray'}
        ></div>
      ),
      getValue(row) {
        return d7Format(row.startDate, 'dd-LLL-yyyy');
      },
      hasSorting: true,
      tdClassName: 'min-w-[16rem]',
      thClassName: 'w-[16.3%] min-w-[16.3%] max-w-[16.3%]',
    },
    {
      name: 'endDate',
      header() {
        return 'End Date';
      },

      loading: () => (
        <div
          className={'h-2 w-[10rem] animate-pulse rounded-[5px] bg-brightGray'}
        ></div>
      ),
      getValue(row) {
        return d7Format(row.endDate, 'dd-LLL-yyyy');
      },
      hasSorting: true,
      tdClassName: 'min-w-[16rem] nowrap',
      thClassName: 'w-[16.3%] min-w-[16rem] max-w-[16.3%]',
    },
    {
      name: 'status',
      header() {
        return 'Status';
      },
      loading: index => {
        const width = Number(index) % 2 ? `w-[${13}rem]` : `w-[${10}rem]`;
        return (
          <div
            className={`h-2 ${width} animate-pulse rounded-md bg-brightGray`}
          ></div>
        );
      },
      getValue(row) {
        return <StatusPill status={row.status} />;
      },
      hasSorting: true,
      tdClassName: '',
      thClassName: 'min-w-[12rem] max-w-[25%]',
    },
    {
      name: 'actions',
      header() {
        return 'Action';
      },
      loading: () => (
        <div className='h-3 w-3 animate-pulse rounded-md bg-brightGray'></div>
      ),
      getValue(row) {
        return (
          <Button
            type='text'
            onClick={() => props.onClinicalTrialClick(row)}
            className='flex h-4 !min-h-fit w-4 cursor-pointer items-center justify-center !p-0'
          >
            <ChevronIcon dir='right' className='h-2 w-2 text-pastelBlue' />
          </Button>
        );
      },
      alignToRight: true,
      cellClassName: 'min-w-[5.7rem]',
      thClassName: 'w-[10rem] z-10',
    },
  ];
}
